// packages/supabase-client/src/react/components/AuthLayout.tsx

import React from 'react';
import { Logo } from '@dopomogai/ui';

export interface AuthLayoutProps {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  brandingSubtitle?: React.ReactNode;
  brandingLogo?: React.ReactNode;
  showBrandPanel?: boolean;
  variant?: 'split' | 'stacked' | 'embedded';
  rootClassName?: string;
  brandPanelClassName?: string;
  formPanelClassName?: string;
  cardClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  children: React.ReactNode;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ 
  title, 
  subtitle, 
  brandingSubtitle = 'Hire once, work forever.',
  brandingLogo,
  showBrandPanel = true,
  variant = 'split',
  rootClassName,
  brandPanelClassName,
  formPanelClassName,
  cardClassName,
  titleClassName,
  subtitleClassName,
  children 
}) => {
  const isSplit = variant === 'split';
  const isStacked = variant === 'stacked';
  const isEmbedded = variant === 'embedded';

  const formPanelWidth = isSplit ? (showBrandPanel ? 'lg:w-1/2' : 'lg:w-full') : 'w-full';
  const rootBase = isEmbedded ? 'w-full' : 'min-h-screen';
  const rootDirection = isSplit ? 'flex-row' : 'flex-col';
  const formPanelBase = isEmbedded ? 'bg-transparent p-0' : 'bg-slate-50 p-4';
  const cardBase = isEmbedded
    ? 'bg-white p-6 border border-slate-200 rounded-xl shadow-sm'
    : 'bg-white p-8 border-2 border-black rounded-xl shadow-[8px_8px_0_0_#e2e8f0]';

  const brandPanelBase = isSplit
    ? 'hidden lg:flex flex-col items-start justify-center w-1/2 bg-emerald-500 text-white p-12 relative'
    : isStacked
      ? 'flex flex-col items-start justify-center w-full bg-emerald-500 text-white px-6 py-10'
      : 'flex flex-col items-start justify-center w-full bg-emerald-500 text-white px-6 py-6 rounded-xl';

  return (
    <div className={["flex font-sans", rootBase, rootDirection, rootClassName].filter(Boolean).join(' ')}>
      {/* Left Branding Panel */}
      {showBrandPanel && (
        <div className={[brandPanelBase, brandPanelClassName].filter(Boolean).join(' ')}>
          <div className="mb-4">
            {brandingLogo ?? <Logo size="lg" logoType="logo+icon" />}
          </div>
          <p className="text-lg text-emerald-100">{brandingSubtitle}</p>
        </div>
      )}

      {/* Right Form Panel */}
      <div
        className={[
          'flex items-center justify-center',
          'w-full',
          formPanelWidth,
          formPanelBase,
          formPanelClassName,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="w-full max-w-md">
            <div className={[cardBase, cardClassName].filter(Boolean).join(' ')}>
                <div className="mb-6">
                    <h2 className={["text-3xl font-bold text-slate-900", titleClassName].filter(Boolean).join(' ')}>{title}</h2>
                    <p className={["text-slate-600 mt-2", subtitleClassName].filter(Boolean).join(' ')}>{subtitle}</p>
                </div>
                {children}
            </div>
        </div>
      </div>
    </div>
  );
};
